/**
 * NovoEditalPage — geração do Edital competitivo (fase Edital).
 *
 * Réplica do new_edital.html: o Edital consolida a cadeia (DFD+ETP+TR) e
 * é o único documento com formulário de parâmetros na criação — modalidade,
 * sessão pública, ME/EPP e qualificação técnica. Processo de credenciamento
 * é desviado para o Chamamento Público (art. 79).
 */

import {
  faArrowLeft,
  faExclamationTriangle,
  faGavel,
  faIdCard,
  faScroll,
  faSpinner,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  gerarEdital,
  novoEdital,
  type ParametrosEdital,
} from '../../api/edital'
import { ApiError } from '../../api/http'
import { useToast } from '../../components/Toast'

const MODALIDADES = [
  ['PREGAO_ELETRONICO', 'Pregão Eletrônico'],
  ['CONCORRENCIA_ELETRONICO', 'Concorrência Eletrônica'],
  ['DIALOGO_COMPETITIVO', 'Diálogo Competitivo'],
  ['LEILAO_ELETRONICO', 'Leilão Eletrônico'],
  ['CONCURSO', 'Concurso'],
] as const

const TIPOS = [
  ['REGISTRO_PRECOS', 'Registro de Preços'],
  ['CONTRATACAO_DIRETA', 'Contratação Direta'],
] as const

const CRITERIOS = [
  ['MENOR_PRECO', 'Menor Preço'],
  ['MAIOR_DESCONTO', 'Maior Desconto'],
  ['MELHOR_TECNICA', 'Melhor Técnica'],
  ['TECNICA_PRECO', 'Técnica e Preço'],
  ['MAIOR_LANCE', 'Maior Lance'],
] as const

const MODOS_DISPUTA = [
  ['ABERTO', 'Aberto'],
  ['FECHADO', 'Fechado'],
  ['ABERTO_FECHADO', 'Aberto e Fechado'],
  ['FECHADO_ABERTO', 'Fechado e Aberto'],
] as const

const DIVISOES = [
  ['ITEM', 'Por Item'],
  ['GRUPO', 'Por Grupo'],
  ['LOTE', 'Por Lote'],
  ['GLOBAL', 'Item Único (Global)'],
] as const

const PADRAO: ParametrosEdital = {
  numero_edital: '',
  modalidade: 'PREGAO_ELETRONICO',
  tipo_licitacao: 'REGISTRO_PRECOS',
  criterio_julgamento: 'MENOR_PRECO',
  modo_disputa: 'ABERTO',
  divisao_itens: 'ITEM',
  data_sessao: '',
  horario_sessao: '09:00',
  uasg: '',
  usa_comprasgov: false,
  plataforma_pregao: 'www.gov.br/compras',
  preferencia_meepp: 'SIM',
  itens_exclusivos_meepp: '',
  objeto_licitacao: '',
  valor_estimado: '',
  registros_profissionais: '',
  formacao_academica: '',
  certificacoes: '',
  experiencia_minima: '',
  outras_exigencias_habilitacao: '',
}

function Campo({
  rotulo, children, largo = false,
}: {
  rotulo: string
  children: React.ReactNode
  largo?: boolean
}) {
  return (
    <label className={`block ${largo ? 'md:col-span-2' : ''}`}>
      <span className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {rotulo}
      </span>
      {children}
    </label>
  )
}

const CLASSE_INPUT =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
      <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
        {titulo}
      </h2>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">{children}</div>
    </section>
  )
}

export function NovoEditalPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const navegar = useNavigate()
  const { notificar } = useToast()
  const [parametros, setParametros] = useState<ParametrosEdital>(PADRAO)

  const { data, isLoading, error } = useQuery({
    queryKey: ['edital-novo', idNumerico],
    queryFn: () => novoEdital(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  const gerar = useMutation({
    mutationFn: () => gerarEdital(idNumerico, parametros),
    onSuccess: () => navegar(`/tasks/edital/${idNumerico}`),
    onError: (e: Error) => notificar('error', e.message),
  })

  const definir = (campo: keyof ParametrosEdital, valor: string | boolean) =>
    setParametros((atual) => ({ ...atual, [campo]: valor }))

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Conferindo a cadeia do processo…
      </p>
    )
  }
  if (error || !data) {
    // processo de credenciamento: o documento certo é o Chamamento
    const detalhes = error instanceof ApiError
      ? (error.detalhes as { credenciamento?: boolean } | undefined)
      : undefined
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon
          icon={detalhes?.credenciamento ? faIdCard : faExclamationTriangle}
          className="mb-4 text-5xl text-amber-400"
        />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          {detalhes?.credenciamento
            ? 'Este processo é de credenciamento'
            : 'Não é possível iniciar o Edital'}
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ?? 'ETP e TR devem ser gerados antes do Edital.'}
        </p>
        <div className="flex justify-center gap-3">
          {detalhes?.credenciamento && (
            <Link
              to={`/credenciamento/novo/${idNumerico}`}
              className="inline-flex items-center rounded-lg bg-teal-600 px-6 py-3 font-semibold text-white hover:bg-teal-700"
            >
              <FontAwesomeIcon icon={faIdCard} className="mr-2" />
              Ir para o Chamamento
            </Link>
          )}
          <Link
            to="/processos"
            className="inline-flex items-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white hover:bg-slate-700"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Voltar
          </Link>
        </div>
      </div>
    )
  }

  return (
    <form
      className="mx-auto max-w-5xl"
      onSubmit={(e) => {
        e.preventDefault()
        gerar.mutate()
      }}
    >
      <header className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faScroll} className="mr-3 text-purple-600" />
          Gerar Edital de Licitação
        </h1>
        <p className="text-slate-600 dark:text-slate-400">{data.objeto}</p>
      </header>

      <section className="mb-6 rounded-xl border border-purple-200 bg-purple-50 p-6 dark:border-purple-800 dark:bg-purple-900/20">
        <h2 className="mb-2 text-lg font-semibold text-purple-800 dark:text-purple-300">
          <FontAwesomeIcon icon={faGavel} className="mr-2" />
          O Edital consolida a cadeia
        </h2>
        <p className="text-sm text-purple-700 dark:text-purple-400">
          As 15 seções do modelo TCU (Art. 25 da Lei 14.133/2021) são geradas a
          partir do DFD, do ETP e do TR já aprovados, com os parâmetros abaixo.
          {data.valor_estimado && (
            <> Valor estimado da contratação (ETP): <strong>{data.valor_estimado}</strong>.</>
          )}
        </p>
      </section>

      <Secao titulo="Dados básicos">
        <Campo rotulo="Número do Edital">
          <input type="text" value={parametros.numero_edital ?? ''} placeholder="Ex.: 001/2026"
            onChange={(e) => definir('numero_edital', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
        <Campo rotulo="Modalidade">
          <select value={parametros.modalidade} onChange={(e) => definir('modalidade', e.target.value)} className={CLASSE_INPUT}>
            {MODALIDADES.map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
          </select>
        </Campo>
        <Campo rotulo="Tipo de licitação">
          <select value={parametros.tipo_licitacao} onChange={(e) => definir('tipo_licitacao', e.target.value)} className={CLASSE_INPUT}>
            {TIPOS.map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
          </select>
        </Campo>
        <Campo rotulo="Critério de julgamento">
          <select value={parametros.criterio_julgamento} onChange={(e) => definir('criterio_julgamento', e.target.value)} className={CLASSE_INPUT}>
            {CRITERIOS.map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
          </select>
        </Campo>
        <Campo rotulo="Modo de disputa">
          <select value={parametros.modo_disputa} onChange={(e) => definir('modo_disputa', e.target.value)} className={CLASSE_INPUT}>
            {MODOS_DISPUTA.map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
          </select>
        </Campo>
        <Campo rotulo="Divisão dos itens">
          <select value={parametros.divisao_itens} onChange={(e) => definir('divisao_itens', e.target.value)} className={CLASSE_INPUT}>
            {DIVISOES.map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
          </select>
        </Campo>
      </Secao>

      <Secao titulo="Sessão pública">
        <Campo rotulo="Data da sessão">
          <input type="date" value={parametros.data_sessao ?? ''}
            onChange={(e) => definir('data_sessao', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
        <Campo rotulo="Horário da sessão">
          <input type="time" value={parametros.horario_sessao ?? ''}
            onChange={(e) => definir('horario_sessao', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
        <Campo rotulo="Plataforma do pregão eletrônico">
          <input type="text" value={parametros.plataforma_pregao ?? ''}
            onChange={(e) => definir('plataforma_pregao', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
        <div>
          <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={Boolean(parametros.usa_comprasgov)}
              onChange={(e) => definir('usa_comprasgov', e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
            />
            Usa o Compras.gov.br (UASG)
          </label>
          {Boolean(parametros.usa_comprasgov) && (
            <input type="text" value={parametros.uasg ?? ''} placeholder="Código UASG"
              aria-label="Código UASG"
              onChange={(e) => definir('uasg', e.target.value)} className={CLASSE_INPUT} />
          )}
        </div>
      </Secao>

      <Secao titulo="ME/EPP (LC 123/2006)">
        <Campo rotulo="Tratamento diferenciado ME/EPP">
          <select value={parametros.preferencia_meepp} onChange={(e) => definir('preferencia_meepp', e.target.value)} className={CLASSE_INPUT}>
            <option value="SIM">SIM - Aplicar tratamento diferenciado</option>
            <option value="NAO">NÃO - Ampla concorrência</option>
          </select>
        </Campo>
        <Campo rotulo="Itens exclusivos ME/EPP">
          <input type="text" value={parametros.itens_exclusivos_meepp ?? ''} placeholder="Ex.: itens 1 a 5"
            onChange={(e) => definir('itens_exclusivos_meepp', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
      </Secao>

      <Secao titulo="Qualificação técnica específica (opcional)">
        <Campo rotulo="Registros profissionais exigidos" largo>
          <input type="text" value={parametros.registros_profissionais ?? ''} placeholder="Ex.: CREA, CRM…"
            onChange={(e) => definir('registros_profissionais', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
        <Campo rotulo="Formação acadêmica">
          <input type="text" value={parametros.formacao_academica ?? ''}
            onChange={(e) => definir('formacao_academica', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
        <Campo rotulo="Certificações">
          <input type="text" value={parametros.certificacoes ?? ''}
            onChange={(e) => definir('certificacoes', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
        <Campo rotulo="Experiência mínima">
          <input type="text" value={parametros.experiencia_minima ?? ''}
            onChange={(e) => definir('experiencia_minima', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
        <Campo rotulo="Outras exigências de habilitação">
          <input type="text" value={parametros.outras_exigencias_habilitacao ?? ''}
            onChange={(e) => definir('outras_exigencias_habilitacao', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
      </Secao>

      <Secao titulo="Objeto e valor (pré-preenchidos da cadeia)">
        <Campo rotulo="Objeto da licitação" largo>
          <textarea rows={3} value={parametros.objeto_licitacao ?? ''} placeholder={data.objeto}
            onChange={(e) => definir('objeto_licitacao', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
        <Campo rotulo="Valor estimado">
          <input type="text" value={parametros.valor_estimado ?? ''} placeholder={data.valor_estimado || 'Ex.: R$ 100.000,00'}
            onChange={(e) => definir('valor_estimado', e.target.value)} className={CLASSE_INPUT} />
        </Campo>
      </Secao>

      {data.tem_edital_gerado && (
        <p className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm font-medium text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          Já existe um Edital gerado neste processo. Gerar novamente substitui
          o conteúdo atual.
        </p>
      )}

      <div className="flex flex-col justify-between gap-3 pb-4 md:flex-row">
        <Link to="/processos" className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-200 px-6 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-300 dark:bg-dark-hover dark:text-slate-200">
          <FontAwesomeIcon icon={faArrowLeft} />
          Voltar
        </Link>
        <button
          type="submit"
          disabled={gerar.isPending}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-purple-700 hover:to-violet-700 disabled:opacity-60"
        >
          <FontAwesomeIcon icon={gerar.isPending ? faSpinner : faWandMagicSparkles} spin={gerar.isPending} />
          Gerar Edital Com LicitIA
        </button>
      </div>
    </form>
  )
}
